import { CampaignPackage } from "@/hooks/useActiveCampaign";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import StockBadge from "./StockBadge";

interface OneOffPackageCardProps {
  pkg: CampaignPackage;
  isAuthenticated: boolean;
  isPurchasing: boolean;
  isUnlimitedUser: boolean;
  hasBooked: boolean | undefined;
  isCheckingEligibility: boolean;
  currentPath: string;
  onPurchase: () => void;
  remaining?: number | null;
}

const OneOffPackageCard = ({
  pkg,
  isAuthenticated,
  isPurchasing,
  isUnlimitedUser,
  hasBooked,
  isCheckingEligibility,
  currentPath,
  onPurchase,
  remaining,
}: OneOffPackageCardProps) => {
  const isFirstTimeOnly = pkg.first_time_only;
  const isEligible = !isFirstTimeOnly || hasBooked === false;
  const isSoldOut = remaining === 0;

  return (
    <Card
      className={`p-6 relative overflow-hidden ${
        !isEligible || isUnlimitedUser || isSoldOut ? "opacity-60" : ""
      }`}
    >
      {/* Badges */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <StockBadge remaining={remaining} />
        {isFirstTimeOnly && (
          <Badge variant="secondary" className="text-xs">
            New Members Only
          </Badge>
        )}
        {pkg.discount_percent && pkg.discount_percent > 40 && (
          <Badge className="bg-secondary text-secondary-foreground text-xs">
            Best Value
          </Badge>
        )}
        {pkg.discount_percent && (
          <Badge variant="outline" className="text-xs">
            {Math.round(pkg.discount_percent)}% OFF
          </Badge>
        )}
      </div>

      <h3 className="text-xl font-bold text-foreground mb-1">{pkg.name}</h3>
      <p className="text-muted-foreground text-sm mb-4 whitespace-pre-line">{pkg.description}</p>

      <div className="space-y-2 mb-4">
        {pkg.credits_amount > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-success" />
            <span>{pkg.credits_amount} credits</span>
          </div>
        )}
        {pkg.includes_nimi_plus && (
          <div className="flex items-center gap-2 text-sm">
            <Crown className="h-4 w-4 text-warning" />
            <span>Nimi+ included</span>
          </div>
        )}
        {pkg.original_value && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Worth £{pkg.original_value}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <span className="text-2xl font-bold text-foreground">£{pkg.price}</span>
          {pkg.original_value && (
            <span className="text-sm text-muted-foreground line-through ml-2">
              £{pkg.original_value}
            </span>
          )}
        </div>

        {isSoldOut ? (
          <Button disabled variant="outline">Sold Out</Button>
        ) : !isAuthenticated ? (
          <Link to={`/auth?redirectTo=${encodeURIComponent(currentPath)}`}>
            <Button variant="secondary">Sign In</Button>
          </Link>
        ) : isUnlimitedUser ? (
          <Button disabled variant="outline">Already Unlimited</Button>
        ) : isFirstTimeOnly && isCheckingEligibility ? (
          <Button disabled variant="outline">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />Checking...
          </Button>
        ) : isEligible ? (
          <Button onClick={onPurchase} disabled={isPurchasing} variant="secondary">
            {isPurchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get Started"}
          </Button>
        ) : (
          <Button disabled variant="outline">Not Eligible</Button>
        )}
      </div>

      {!isEligible && !isSoldOut && (
        <p className="text-xs text-muted-foreground mt-2">
          This offer is for new members only
        </p>
      )}
    </Card>
  );
};

export default OneOffPackageCard;
